/** Support analytics and user-behaviour reporting. */
const en = {
  support: {
    title: 'Support Analytics',
    subtitle: 'Session intelligence & support performance metrics',
    filters: {
      organization: 'Organization',
      module: 'Module',
      allModules: 'All modules',
      version: 'Version',
      allVersions: 'All versions',
      segment: 'Segment',
      allSegments: 'All segments',
    },
    kpi: {
      frustration: 'Avg Frustration Score',
      rage: 'Rage Click Rate',
      dead: 'Dead Click Rate',
      ticketRate: 'Session → Ticket Rate',
    },
    drill: {
      frustrated: 'Highly frustrated sessions',
      frustratedHint: 'Frustration score ≥ 40',
      rage: 'Sessions with rage clicks',
      dead: 'Sessions with dead clicks',
      lastPage: 'Sessions whose last page was this path',
    },
    anomalyTitle: 'Click Anomaly Trends',
    anomalySubtitle: 'Rage & dead click rates over time',
    rageSeries: 'Rage clicks',
    deadSeries: 'Dead clicks',
    resolutionTitle: 'Avg Resolution Time',
    resolutionSubtitle: 'With vs without session replay',
    avgResolution: 'Avg resolution',
    ai: 'AI',
    heuristic: 'Heuristic',
    analyzing: 'Analyzing…',
    reanalyze: 'Re-analyze',
    analyze: 'Analyze',
    analysisFailed: 'Analysis failed. Try again.',
    problemPages: 'Most Problematic Pages',
    problemPagesSubtitle: 'By error count & frustration score',
    tickets: {
      total: 'Total tickets',
      open: 'Open',
      resolved: 'Resolved',
      fromReplay: 'From a replay',
      createdVsResolved: 'Created vs resolved',
      created: 'Created',
      resolvedSeries: 'Resolved',
      byStatus: 'By status',
      byPriority: 'By priority',
      workload: 'Workload by assignee',
      assignee: 'Assignee',
      medianResolution: 'Median resolution',
      unassigned: 'Unassigned',
      none: 'No tickets yet.',
    },
    noErrors: 'No errors captured in this window.',
    heatCell: (day: string, hour: number, n: number) =>
      `${day} ${hour}:00 · ${n} error(s) · click to view sessions`,
    jsErrors: 'JS errors',
    rageClicks: 'Rage clicks',
    noData: 'No data.',
  },
  behavior: {
    title: 'User Behavior',
    subtitle: 'Frustration signals, click anomalies, and behavioral patterns across sessions',
    kpi: {
      frustration: 'Avg Frustration',
      rage: 'Rage Clicks',
      dead: 'Dead Clicks',
      avgSession: 'Avg Session',
    },
    trendTitle: 'Frustration Score Trend',
    trendSubtitle: 'Daily average frustration across all sessions',
    avgFrustration: 'Avg frustration',
    activityTitle: 'Session Activity',
    activitySubtitle: 'Sessions by hour (UTC)',
    sessions: 'Sessions',
    distribution: 'Frustration distribution',
    band: (label: string) => `Frustration ${label}, sessions`,
    bandHint: 'Sessions in this frustration band',
    sourceSessions: (label: string) => `${label}, sessions`,
    sourceHint: 'Sessions from this source',
    deviceHint: 'Sessions on this device type',
    entryHint: 'Sessions that started on this page',
    lastPageHint: 'Sessions whose last page was this',
    trafficSources: 'Traffic sources',
    byChannel: 'By channel',
    countries: 'Countries',
    devices: 'Devices',
    browsers: 'Browsers',
    entryPages: 'Top entry pages',
    anomaliesByPage: 'Click Anomalies by Page',
    noAnomalies: 'No anomalies in this range.',
    dropoffs: 'Top Drop-off Points',
    notEnoughJourney: 'Not enough journey data yet.',
    frustratedUsers: 'Most Frustrated Users',
    columns: {
      user: 'User',
      account: 'Account',
      frustration: 'Frustration',
      sessions: 'Sessions',
      rageClicks: 'Rage clicks',
    },
    noData: 'No data.',
  },
};

const tr: typeof en = {
  support: {
    title: 'Destek Analitiği',
    subtitle: 'Oturum zekâsı ve destek performans metrikleri',
    filters: {
      organization: 'Organizasyon',
      module: 'Modül',
      allModules: 'Tüm modüller',
      version: 'Sürüm',
      allVersions: 'Tüm sürümler',
      segment: 'Segment',
      allSegments: 'Tüm segmentler',
    },
    kpi: {
      frustration: 'Ort. zorlanma puanı',
      rage: 'Öfke tıklama oranı',
      dead: 'Ölü tıklama oranı',
      ticketRate: 'Oturum → Talep oranı',
    },
    drill: {
      frustrated: 'Yüksek zorlanmalı oturumlar',
      frustratedHint: 'Zorlanma puanı ≥ 40',
      rage: 'Öfke tıklaması olan oturumlar',
      dead: 'Ölü tıklaması olan oturumlar',
      lastPage: 'Son sayfası bu yol olan oturumlar',
    },
    anomalyTitle: 'Tıklama anomali eğilimleri',
    anomalySubtitle: 'Zaman içinde öfke ve ölü tıklama oranları',
    rageSeries: 'Öfke tıklamaları',
    deadSeries: 'Ölü tıklamalar',
    resolutionTitle: 'Ort. çözüm süresi',
    resolutionSubtitle: 'Oturum kaydı olan ve olmayan',
    avgResolution: 'Ort. çözüm',
    ai: 'AI',
    heuristic: 'Sezgisel',
    analyzing: 'Analiz ediliyor…',
    reanalyze: 'Yeniden analiz et',
    analyze: 'Analiz et',
    analysisFailed: 'Analiz başarısız. Tekrar deneyin.',
    problemPages: 'En sorunlu sayfalar',
    problemPagesSubtitle: 'Hata sayısı ve zorlanma puanına göre',
    tickets: {
      total: 'Toplam talep',
      open: 'Açık',
      resolved: 'Çözüldü',
      fromReplay: 'Bir kayıttan',
      createdVsResolved: 'Açılan ve çözülen',
      created: 'Açılan',
      resolvedSeries: 'Çözülen',
      byStatus: 'Duruma göre',
      byPriority: 'Önceliğe göre',
      workload: 'Atanana göre iş yükü',
      assignee: 'Atanan',
      medianResolution: 'Medyan çözüm',
      unassigned: 'Atanmamış',
      none: 'Henüz talep yok.',
    },
    noErrors: 'Bu aralıkta yakalanan hata yok.',
    heatCell: (day: string, hour: number, n: number) =>
      `${day} ${hour}:00 · ${n} hata · oturumları görmek için tıklayın`,
    jsErrors: 'JS hataları',
    rageClicks: 'Öfke tıklamaları',
    noData: 'Veri yok.',
  },
  behavior: {
    title: 'Kullanıcı Davranışı',
    subtitle: 'Oturumlar genelinde zorlanma sinyalleri, tıklama anomalileri ve davranış örüntüleri',
    kpi: {
      frustration: 'Ort. zorlanma',
      rage: 'Öfke tıklamaları',
      dead: 'Ölü tıklamalar',
      avgSession: 'Ort. oturum',
    },
    trendTitle: 'Zorlanma puanı eğilimi',
    trendSubtitle: 'Tüm oturumlarda günlük ortalama zorlanma',
    avgFrustration: 'Ort. zorlanma',
    activityTitle: 'Oturum hareketliliği',
    activitySubtitle: 'Saate göre oturumlar (UTC)',
    sessions: 'Oturum',
    distribution: 'Zorlanma dağılımı',
    band: (label: string) => `Zorlanma ${label}, oturumlar`,
    bandHint: 'Bu zorlanma aralığındaki oturumlar',
    sourceSessions: (label: string) => `${label}, oturumlar`,
    sourceHint: 'Bu kaynaktan gelen oturumlar',
    deviceHint: 'Bu cihaz türündeki oturumlar',
    entryHint: 'Bu sayfadan başlayan oturumlar',
    lastPageHint: 'Son sayfası bu olan oturumlar',
    trafficSources: 'Trafik kaynakları',
    byChannel: 'Kanala göre',
    countries: 'Ülkeler',
    devices: 'Cihazlar',
    browsers: 'Tarayıcılar',
    entryPages: 'En çok giriş yapılan sayfalar',
    anomaliesByPage: 'Sayfaya göre tıklama anomalileri',
    noAnomalies: 'Bu aralıkta anomali yok.',
    dropoffs: 'En çok terk edilen noktalar',
    notEnoughJourney: 'Henüz yeterli yolculuk verisi yok.',
    frustratedUsers: 'En çok zorlanan kullanıcılar',
    columns: {
      user: 'Kullanıcı',
      account: 'Hesap',
      frustration: 'Zorlanma',
      sessions: 'Oturum',
      rageClicks: 'Öfke tıklaması',
    },
    noData: 'Veri yok.',
  },
};

export const insights = { en, tr };
